import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Download, Printer, ArrowSquareOut } from "@phosphor-icons/react";
import { getOrderInvoice } from "@/lib/orders.functions";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import bananaLogo from "@/assets/banana-cards-logo.png";

export const Route = createFileRoute("/orders/$id_/invoice")({
  head: () => ({
    meta: [
      { title: "Invoice — banana.cards" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: OrderInvoicePage,
});

type IssuerBlock = {
  legal_name?: string;
  name?: string;
  tax_id?: string;
  address?: string;
  email?: string;
  phone?: string;
  website?: string;
};

type BuyerBlock = {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  tax_id?: string;
};

type LineItem = {
  product_name?: string;
  name?: string;
  description?: string;
  quantity?: number;
  qty?: number;
  unit_price?: number;
  price?: number;
  total?: number;
  amount?: number;
};

function formatMoney(amount: number | null | undefined, currency: string) {
  const n = Number(amount ?? 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: currency === "VND" ? 0 : 2,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
}

function formatDate(iso?: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function OrderInvoicePage() {
  const { id } = Route.useParams();
  const fetchInvoice = useServerFn(getOrderInvoice);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["order-invoice", id],
    queryFn: () => fetchInvoice({ data: { id } }),
    // Poll softly while the invoice is still being generated by the pipeline.
    refetchInterval: (q) =>
      (q.state.data as any)?.kind === "pending" ? 5000 : false,
  });

  // Stripe orders: bounce to the hosted invoice URL.
  useEffect(() => {
    if (data && (data as any).kind === "stripe") {
      window.location.replace((data as any).hostedUrl);
    }
  }, [data]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border print:hidden">
        <div className="mx-auto max-w-3xl px-4 py-3 sm:py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2">
            <img src={bananaLogo} alt="banana.cards" className="h-3.5" />
          </Link>
          {data && (data as any).kind === "invoice" && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.print()}
                className="rounded-xl"
              >
                <Printer className="h-4 w-4 mr-1.5" weight="bold" />
                Print
              </Button>
              <Button
                size="sm"
                onClick={() => window.print()}
                className="rounded-xl"
              >
                <Download className="h-4 w-4 mr-1.5" weight="bold" />
                Download PDF
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-6 sm:py-10">
        {isLoading && (
          <div className="space-y-4">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <div className="rounded-2xl bg-muted p-6 sm:p-8 space-y-4 mt-6">
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
              <div className="pt-4 space-y-3">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            </div>
          </div>
        )}

        {isError && (
          <div className="rounded-2xl bg-muted px-6 py-10 text-center">
            <p className="text-sm text-muted-foreground">
              We couldn't load this invoice. Please make sure you're signed in
              with the account that placed the order.
            </p>
          </div>
        )}

        {data && (data as any).kind === "stripe" && (
          <div className="rounded-2xl bg-muted px-6 py-10 text-center">
            <p className="text-sm text-muted-foreground mb-4">
              Redirecting you to the hosted Stripe invoice…
            </p>
            <a
              href={(data as any).hostedUrl}
              className="inline-flex items-center gap-1.5 text-sm font-bold underline"
            >
              Open invoice <ArrowSquareOut className="h-4 w-4" weight="bold" />
            </a>
          </div>
        )}

        {data && (data as any).kind === "pending" && (
          <div className="rounded-2xl bg-muted px-6 py-10 text-center">
            <p className="text-sm text-muted-foreground">
              Your invoice is being generated. This usually takes a couple of
              minutes — this page will refresh automatically.
            </p>
          </div>
        )}

        {data && (data as any).kind === "invoice" && (
          <InvoiceView data={data as any} />
        )}
      </main>
    </div>
  );
}

function InvoiceView({
  data,
}: {
  data: {
    invoice: {
      invoice_number: string;
      issued_at: string;
      currency: string;
      subtotal: number;
      shipping: number;
      discount: number;
      tax: number;
      total: number;
      issuer: IssuerBlock;
      buyer: BuyerBlock;
      line_items: LineItem[];
      jurisdiction: string;
    };
    order: {
      order_number: string;
      paid_at?: string | null;
      created_at: string;
      payment_method_code?: string | null;
    };
  };
}) {
  const { invoice, order } = data;
  const currency = invoice.currency || "USD";
  const items = Array.isArray(invoice.line_items) ? invoice.line_items : [];
  const issuer = invoice.issuer || {};
  const buyer = invoice.buyer || {};

  return (
    <article className="rounded-2xl bg-muted p-6 sm:p-10 print:bg-white print:p-0 print:rounded-none">
      <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6 pb-6 border-b border-border/60">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl">Invoice</h1>
          <div className="mt-2 text-sm text-muted-foreground">
            <div>
              Invoice number:{" "}
              <span className="font-mono font-bold text-foreground">
                {invoice.invoice_number}
              </span>
            </div>
            <div>
              Order number:{" "}
              <span className="font-mono font-bold text-foreground">
                {order.order_number}
              </span>
            </div>
            <div>Issued: {formatDate(invoice.issued_at)}</div>
            {order.paid_at && <div>Paid: {formatDate(order.paid_at)}</div>}
          </div>
        </div>
        <div className="text-sm sm:text-right">
          <div className="font-bold text-foreground">
            {issuer.legal_name || issuer.name || "banana.cards"}
          </div>
          {issuer.tax_id && (
            <div className="text-muted-foreground">Tax ID: {issuer.tax_id}</div>
          )}
          {issuer.address && (
            <div className="text-muted-foreground whitespace-pre-line">
              {issuer.address}
            </div>
          )}
          {issuer.email && (
            <div className="text-muted-foreground">{issuer.email}</div>
          )}
          {issuer.phone && (
            <div className="text-muted-foreground">{issuer.phone}</div>
          )}
        </div>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-6 border-b border-border/60">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
            Billed to
          </div>
          <div className="text-sm">
            <div className="font-bold text-foreground">{buyer.name || "—"}</div>
            {buyer.email && (
              <div className="text-muted-foreground">{buyer.email}</div>
            )}
            {buyer.phone && (
              <div className="text-muted-foreground">{buyer.phone}</div>
            )}
            {buyer.address && (
              <div className="text-muted-foreground whitespace-pre-line">
                {buyer.address}
              </div>
            )}
            {buyer.tax_id && (
              <div className="text-muted-foreground">
                Tax ID: {buyer.tax_id}
              </div>
            )}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
            Payment
          </div>
          <div className="text-sm">
            <div className="text-foreground">
              {order.payment_method_code
                ? String(order.payment_method_code).replace(/_/g, " ")
                : "—"}
            </div>
            <div className="text-muted-foreground">Currency: {currency}</div>
          </div>
        </div>
      </section>

      <section className="py-6">
        <div className="overflow-x-auto -mx-2 sm:mx-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-3">Description</th>
                <th className="py-2 px-3 text-right w-16">Qty</th>
                <th className="py-2 px-3 text-right w-28">Unit</th>
                <th className="py-2 pl-3 text-right w-28">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="py-6 text-center text-muted-foreground"
                  >
                    No line items on this invoice.
                  </td>
                </tr>
              )}
              {items.map((it, idx) => {
                const name = it.product_name || it.name || "Item";
                const qty = Number(it.quantity ?? it.qty ?? 1);
                const unit = Number(
                  it.unit_price ??
                    it.price ??
                    (it.total != null ? Number(it.total) / (qty || 1) : 0),
                );
                const total = Number(it.total ?? it.amount ?? unit * qty);
                return (
                  <tr key={idx} className="align-top">
                    <td className="py-3 pr-3">
                      <div className="font-bold text-foreground">{name}</div>
                      {it.description && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {it.description}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-3 text-right tabular-nums">{qty}</td>
                    <td className="py-3 px-3 text-right tabular-nums">
                      {formatMoney(unit, currency)}
                    </td>
                    <td className="py-3 pl-3 text-right tabular-nums font-bold">
                      {formatMoney(total, currency)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="pt-2 border-t border-border/60">
        <dl className="ml-auto max-w-xs text-sm space-y-1.5 pt-4">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Subtotal</dt>
            <dd className="tabular-nums">
              {formatMoney(invoice.subtotal, currency)}
            </dd>
          </div>
          {Number(invoice.discount) > 0 && (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Discount</dt>
              <dd className="tabular-nums">
                −{formatMoney(invoice.discount, currency)}
              </dd>
            </div>
          )}
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Shipping</dt>
            <dd className="tabular-nums">
              {formatMoney(invoice.shipping, currency)}
            </dd>
          </div>
          {Number(invoice.tax) > 0 && (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Tax</dt>
              <dd className="tabular-nums">
                {formatMoney(invoice.tax, currency)}
              </dd>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t border-border/60 text-base">
            <dt className="font-bold">Total</dt>
            <dd className="tabular-nums font-bold">
              {formatMoney(invoice.total, currency)}
            </dd>
          </div>
        </dl>
      </section>

      <footer className="mt-8 pt-6 border-t border-border/60 text-xs text-muted-foreground">
        Thank you for your order. If you need any changes to this invoice, reply
        to your order confirmation email and our team will help.
      </footer>
    </article>
  );
}